"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { Loader, CircleCheck } from "lucide-react";
export default function BespreekUwSituatie() {
  return <section id="bespreek-uw-situatie" className="dark bg-background text-foreground py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" aria-hidden="true" />

      <div className="max-w-4xl mx-auto relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-serif text-foreground mb-4 tracking-tight">
            Bespreek uw situatie
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Vul het onderstaande formulier in voor een vrijblijvend adviesgesprek. Wij luisteren naar uw verhaal en denken graag mee over passende zorg en ondersteuning.
          </p>
        </div>

        <div className="bg-card text-card-foreground p-6 md:p-10 rounded-3xl shadow-2xl border border-border/50">
          <FormProvider formId="situation_form_how_we_work" sectionName="bespreek-uw-situatie" defaultValues={{
          name: "",
          email: "",
          phone: "",
          role: "",
          situation: "",
          preferred_contact: "E-mail",
          best_time: ""
        }} validationRules={{
          name: {
            required: "Uw naam is verplicht"
          },
          email: {
            required: "E-mailadres is verplicht",
            pattern: "email"
          },
          role: {
            required: "Selecteer uw rol"
          },
          situation: {
            required: "Beschrijf uw situatie"
          },
          preferred_contact: {
            required: "Selecteer een contactmethode"
          }
        }} submitText="Verstuur aanvraag" successMessage="Bedankt voor uw bericht. Wij nemen zo spoedig mogelijk contact met u op." errorMessage="Er is een fout opgetreden bij het versturen. Probeer het later opnieuw.">
            <SituationForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function SituationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-16 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-20 bg-primary/20 text-primary rounded-full flex items-center justify-center mb-4">
          <CircleCheck className="size-10" />
        </div>
        <h3 className="text-3xl font-serif text-foreground">Aanvraag verzonden</h3>
        <p className="text-lg text-muted-foreground max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-8 h-12 px-6 rounded-full">
          Nieuwe aanvraag starten
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="name" render={({
          field
        }) => <FormItem>
                <FormLabel>Uw naam</FormLabel>
                <FormControl>
                  <Input placeholder="Jan Jansen" className="h-12 bg-input/50 border-border/50 focus-visible:ring-primary" {...field} />
                </FormControl>
                <FormDescription>Vul hier uw volledige naam in.</FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" render={({
          field
        }) => <FormItem>
                <FormLabel>E-mail adres</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="jan@example.com" className="h-12 bg-input/50 border-border/50 focus-visible:ring-primary" {...field} />
                </FormControl>
                <FormDescription>We nemen via e-mail contact met u op.</FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="phone" render={({
          field
        }) => <FormItem>
                <FormLabel>Telefoonnummer</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="+31 6 1234 5678" className="h-12 bg-input/50 border-border/50 focus-visible:ring-primary" {...field} />
                </FormControl>
                <FormDescription>Optioneel, voor een sneller telefonisch contact.</FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="role" render={({
          field
        }) => <FormItem>
                <FormLabel>Uw relatie tot de cliënt</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className="h-12 bg-input/50 border-border/50 focus-visible:ring-primary">
                      <SelectValue placeholder="Selecteer uw rol" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {["Familielid", "Mantelzorger", "Huisarts", "Casemanager", "Wijkteam", "Gemeente", "Verwijzer", "Anders"].map((role, i) => <SelectItem key={i} value={role} data-index={i}>{role}</SelectItem>)}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="situation" render={({
        field
      }) => <FormItem>
              <FormLabel>Beschrijf uw situatie</FormLabel>
              <FormControl>
                <Textarea placeholder="Vertel kort over de zorgbehoefte, wensen en eventuele uitdagingen..." className="min-h-[140px] resize-y bg-input/50 border-border/50 focus-visible:ring-primary text-base p-4" {...field} />
              </FormControl>
              <FormDescription>Hoe meer details, hoe beter wij u kunnen helpen.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4 border-t border-border/50">
          <FormField control={control} name="preferred_contact" render={({
          field
        }) => <FormItem className="space-y-4">
                <FormLabel>Gewenste contactmethode</FormLabel>
                <FormControl>
                  <RadioGroup onValueChange={field.onChange} defaultValue={field.value} className="flex flex-col space-y-2">
                    {["E-mail", "Telefoon", "Beide"].map((method, i) => <FormItem key={i} className="flex items-center space-x-3 space-y-0 bg-input/30 p-3 rounded-lg border border-border/30" data-index={i}>
                        <FormControl>
                          <RadioGroupItem value={method} className="text-primary border-primary" />
                        </FormControl>
                        <FormLabel className="font-normal cursor-pointer w-full">
                          {method}
                        </FormLabel>
                      </FormItem>)}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="best_time" render={({
          field
        }) => <FormItem>
                <FormLabel>Beste tijd om te bellen (indien telefoon)</FormLabel>
                <FormControl>
                  <Input placeholder="bijv. 14:00 – 16:00" className="h-12 bg-input/50 border-border/50 focus-visible:ring-primary" {...field} />
                </FormControl>
                <FormDescription>Vermeld een moment waarop u bereikbaar bent.</FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>

        {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-xl text-destructive text-sm flex items-center">
            <div className="size-2 rounded-full bg-destructive mr-3" />
            {submitError}
          </div>}

        <div className="pt-6 flex justify-end">
          <Button type="submit" disabled={isSubmitting} size="lg" className="w-full md:w-auto md:min-w-[240px] h-14 text-base rounded-full shadow-lg shadow-primary/20 transition-transform hover:-translate-y-1">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-5" />
                Verzenden...
              </> : 'Verstuur aanvraag'}
          </Button>
        </div>
      </div>
    </Form>;
}